import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import { AppBar,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";

import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyIcon from "@mui/icons-material/Key";
import MenuIcon from "@mui/icons-material/Menu";
import { useCallback, useContext, useEffect, useState } from "react";

import NotificationSystem from "./Notifications";
import UserProfile from "./UserProfile";
import {
  getAccessToken,
  getCompanyCode,
  getCompanyCodes,
  getImagePath,
  getRole,
  getUsername,
  hasSession,
  isAssignedCompany,
  logoutSession,
  redirectToLogin,
  setCompanyCode,
} from "../auth/authSession";
import { PermissionContext } from "../PermissionContext";
import { notifyDepartmentsChanged } from "../utils/procurementMode";
import { BRAND_NAVY } from "../theme";
import LOGO from "./CLlogo2.jpg";

const LAST_COMPANY_KEY = "lastCompany";

const getInitials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Header = ({ onMenuClick }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const decryptedCompanyCode = getCompanyCode();
  const decryptedUsername = getUsername();
  const decryptedImagepath = getImagePath();
  const decryptedRole = getRole();
  const isMdUp = useMediaQuery("(min-width:900px)");
  const { permissions = [] } = useContext(PermissionContext) || {};
  const assignedCodes = getCompanyCodes();
  const canSwitchCompany = assignedCodes.length > 1;

  if (!hasSession() || !decryptedCompanyCode || !decryptedUsername) {
    redirectToLogin();
  }

  const navigate = useNavigate();
  const [imageSrc, setImageSrc] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [companyAnchor, setCompanyAnchor] = useState(null);
  const [companyNames, setCompanyNames] = useState({});
  const [open, setOpen] = useState(false);

  const handleLogout = async (e) => {
    e.preventDefault();
    await logoutSession();
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    handleCloseMenu();
    navigate("/change-password", { state: { reason: "voluntary" } });
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const switchCompany = (code) => {
    setCompanyAnchor(null);
    if (!code || code === decryptedCompanyCode) return;
    if (!isAssignedCompany(code) || !setCompanyCode(code)) return;
    sessionStorage.setItem(LAST_COMPANY_KEY, code);
    const securityOnly =
      (permissions.includes("approve_security_out") ||
        permissions.includes("approve_security_in")) &&
      !permissions.includes("view_product");
    const target = securityOnly ? "/security-repairs" : "/home";
    notifyDepartmentsChanged();
    if (window.location.pathname === target && window.location.search) {
      window.location.assign(target);
      return;
    }
    if (window.location.pathname === target) {
      window.location.reload();
      return;
    }
    navigate(target);
  };

  useEffect(() => {
    if (!canSwitchCompany) return undefined;
    let cancelled = false;
    const loadCompanyNames = async () => {
      try {
        const response = await fetch(`${apiUrl}/api/company/all`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled || !Array.isArray(data)) return;
        const names = {};
        data.forEach((company) => {
          if (company?.code) names[company.code] = company.name || company.code;
        });
        setCompanyNames(names);
      } catch (error) {
        console.error("Failed to load company names:", error);
      }
    };
    loadCompanyNames();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, canSwitchCompany]);

  const fetchImage = useCallback(async () => {
    const token = getAccessToken();
    const cleanPath = decryptedImagepath.replace(/^"|"$/g, "");
    const imageUrl = `${apiUrl}${cleanPath}`;

    try {
      const response = await fetch(imageUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch image");
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error fetching image:", error);
      return null;
    }
  }, [apiUrl, decryptedImagepath]);

  useEffect(() => {
    const loadImage = async () => {
      const url = await fetchImage();
      setImageSrc(url);
    };

    loadImage();
  }, [decryptedImagepath, fetchImage]);

  const handleViewClick = () => {
    handleCloseMenu();
    setOpen(true);
  };

  const handleViewClose = () => {
    setOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: "100%",
        backgroundColor: BRAND_NAVY,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        {!isMdUp && (
          <IconButton color="inherit" onClick={onMenuClick} aria-label="Open menu">
            <MenuIcon />
          </IconButton>
        )}
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: "#ffffff",
            display: "grid",
            placeItems: "center",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <Box
            component="img"
            src={LOGO}
            alt="Colombo Logistics"
            sx={{ width: 26, height: 26, objectFit: "contain" }}
          />
        </Box>
        {canSwitchCompany ? (
          <Button
            color="inherit"
            onClick={(event) => setCompanyAnchor(event.currentTarget)}
            endIcon={<KeyboardArrowDownIcon />}
            aria-label="Switch company"
            sx={{
              color: "#ffffff",
              fontWeight: 700,
              fontSize: "1.25rem",
              textTransform: "none",
              px: 1,
              minWidth: 0,
            }}
          >
            {decryptedCompanyCode}
          </Button>
        ) : (
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#ffffff",
            }}
          >
            {decryptedCompanyCode}
          </Typography>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {decryptedUsername !== "Security" && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <NotificationSystem />
          </Box>
        )}

        <Button
          color="inherit"
          onClick={handleMenuClick}
          aria-label="Account menu"
          aria-haspopup="true"
          aria-expanded={Boolean(anchorEl)}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            color: "#ffffff",
            textTransform: "none",
            px: 1,
            minWidth: 0,
            gap: 0.5,
          }}
        >
          <Avatar
            src={imageSrc || undefined}
            alt={decryptedUsername}
            sx={{
              width: 32,
              height: 32,
              fontSize: 13,
              fontWeight: 600,
              bgcolor: "rgba(255,255,255,0.18)",
              color: "#ffffff",
              border: "1px solid rgba(255,255,255,0.45)",
            }}
          >
            {getInitials(decryptedUsername)}
          </Avatar>

          <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left", ml: 1 }}>
            <Typography variant="body2" sx={{ color: "#ffffff", lineHeight: 1.2 }}>
              {decryptedUsername}
            </Typography>
            {decryptedRole && (
              <Typography
                variant="caption"
                sx={{ color: "rgba(255,255,255,0.72)", lineHeight: 1.2, display: "block" }}
              >
                {decryptedRole.replace(/_/g, " ")}
              </Typography>
            )}
          </Box>
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={handleViewClick}>
            <ListItemIcon>
              <AccountCircleOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleChangePassword}>
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change password</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <PowerSettingsNewOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sign out</ListItemText>
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={companyAnchor}
          open={Boolean(companyAnchor)}
          onClose={() => setCompanyAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          {assignedCodes.map((code) => {
            const name = companyNames[code];
            const label = name && name !== code ? `${name} (${code})` : code;
            return (
              <MenuItem
                key={code}
                selected={code === decryptedCompanyCode}
                onClick={() => switchCompany(code)}
              >
                <ListItemText>{label}</ListItemText>
              </MenuItem>
            );
          })}
        </Menu>

      </Toolbar>
      <Dialog
        open={open}
        onClose={() => {}}
        sx={{
          zIndex: 1300,
          "& .MuiDialog-paper": {
            maxWidth: "90%",
            width: "100%",
            minHeight: "400px",
            height: "90%",
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AccountCircleOutlinedIcon sx={{ marginRight: 1 }} /> Employee
            Profile
          </Box>
          <hr />
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleViewClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#000",
            }}
          >
            <CloseIcon sx={{ fontSize: 30, stroke: "#000", strokeWidth: 1 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <UserProfile setOpen={setOpen} />
        </DialogContent>
      </Dialog>
    </AppBar>
  );
};

export default Header;
